import {
  GalleryImage,
  getGalleryImages,
  possibleMonths,
  buildCorrectionsFormUrl,
} from "./galleries";

const galleriesBase = "https://assets.richmondwoodturners.org/galleries/";
const swipeThresholdPx = 50;
const topFadeDelayMs = 150;
const viewerSlideDurationMs = 300;

interface SlideshowState {
  images: GalleryImage[];
  index: number;
  subIndex: number;
  focused: boolean;
}

interface SlideshowElements {
  viewer: HTMLDivElement;
  top: HTMLDivElement;
  info: HTMLDivElement;
  mainImg: HTMLImageElement;
  subthumbs: HTMLDivElement;
  subthumbElements: HTMLDivElement[];
  filmstripThumbs: HTMLDivElement[];
}

let slideshowState: SlideshowState | null = null;
let slideshowElements: SlideshowElements | null = null;

function toThumbPath(path: string): string {
  return path.replace(/\.jpe?g$/i, "-thumb.jpg");
}

function toFullUrl(path: string): string {
  return `${galleriesBase}${path}`;
}

function getSubImagePaths(image: GalleryImage): string[] {
  return [image.primary_image, ...(image.secondary_images || [])];
}

function createGridHeader(
  container: Element,
  type: string,
  year: string | null,
  month: string | null,
  name: string | null,
) {
  const header = document.createElement("h2");
  if (type === "show_and_tell") {
    const monthName = (month && possibleMonths[month]) || month;
    header.textContent = `Show and Tell—${monthName} ${year}`;
  } else if (type === "competitions") {
    header.textContent = `Turning Competition—${year}`;
  } else if (type === "members" && name) {
    header.textContent = `Member Gallery—${name}`;
  } else if (type === "tags" && name) {
    header.textContent = `Tag Gallery—${name}`;
  } else {
    header.textContent = `Gallery`;
  }
  container.appendChild(header);
}

function createPhotoHelpText(container: Element) {
  const help = document.createElement("p");
  help.classList.add("photohelp");
  help.textContent =
    "Click a thumbnail to get a blow-up, then use left/right arrow keys to step through the pictures. Press Escape to return to the thumbnails.";
  container.appendChild(help);
}

function fillGridWithThumbnails(container: Element, images: GalleryImage[]) {
  const div = document.createElement("div");
  div.classList.add("gallery-grid");

  images.forEach((image, index) => {
    const thumbnail = createGridThumbnailElement(image, images, index);
    div.appendChild(thumbnail);
  });

  container.appendChild(div);
}

function createGridThumbnailElement(
  image: GalleryImage,
  images: GalleryImage[],
  index: number,
): HTMLDivElement {
  const div = document.createElement("div");
  div.classList.add("gallery-thumbnail");

  const img = document.createElement("img");
  img.src = `${galleriesBase}${image.thumb || image.primary_image}`;
  img.alt = image.description || "";
  div.appendChild(img);

  div.addEventListener("click", () => openSlideshow(images, index));

  return div;
}

export function populateGallerySection() {
  const containers = document.querySelectorAll(".gallery-grid-section");
  if (containers) {
    const params = new URLSearchParams(window.location.search);
    let deepLinkOpened = false;

    containers.forEach(async (container) => {
      const type =
        params.get("type") ??
        container.getAttribute("data-type") ??
        "show_and_tell";
      let year = params.get("year") ?? container.getAttribute("data-year");
      let month = params.get("month") ?? container.getAttribute("data-month");
      const name = params.get("name") ?? container.getAttribute("data-name");

      const now = new Date();
      if (type === "show_and_tell" && (year === null || month === null)) {
        year = `${now.getFullYear()}`;
        month = `${now.getMonth() + 1}`.padStart(2, "0");
      } else if (type === "competitions" && year === null) {
        year = `${now.getFullYear()}`;
      }

      createGridHeader(container, type, year, month, name);
      createPhotoHelpText(container);

      const images = await getGalleryImages(type, year, month, name);
      fillGridWithThumbnails(container, images);

      if (!deepLinkOpened && images.length > 0 && params.get("slideshow") === "open") {
        deepLinkOpened = true;
        openSlideshowFromUrlParams(images, params);
      }
    });
  }
}

function clampIndex(index: number, length: number): number {
  if (isNaN(index) || length === 0) return 0;
  return Math.min(Math.max(index, 0), length - 1);
}

function openSlideshowFromUrlParams(
  images: GalleryImage[],
  params: URLSearchParams,
) {
  const imageIndex = clampIndex(
    parseInt(params.get("image") ?? "1", 10) - 1,
    images.length,
  );
  const subImageCount = getSubImagePaths(images[imageIndex]).length;
  const subIndex = clampIndex(
    parseInt(params.get("subimage") ?? "1", 10) - 1,
    subImageCount,
  );

  // Deep-linking straight into a slide should land on it immediately
  // rather than sliding in, since there's no preceding thumbnail click to
  // animate from.
  openSlideshow(images, imageIndex, subIndex, true);
}

function openSlideshow(
  images: GalleryImage[],
  startIndex: number,
  startSubIndex: number = 0,
  skipOpenAnimation: boolean = false,
) {
  slideshowState = {
    images,
    index: startIndex,
    subIndex: startSubIndex,
    focused: false,
  };
  slideshowElements = buildSlideshowElements(images);

  document.body.appendChild(slideshowElements.viewer);
  document.addEventListener("keydown", handleSlideshowKeydown);

  updateSlideshowImage();
  updateSlideshowUrlParams(startIndex, startSubIndex);

  const viewer = slideshowElements.viewer;

  if (skipOpenAnimation) {
    viewer.style.transition = "none";
    viewer.classList.add("open");
    // Force layout so the transform change above is applied while the
    // transition is disabled, before we restore it below.
    viewer.getBoundingClientRect();
    viewer.style.transition = "";
    return;
  }

  // Force layout so the viewer's off-screen starting position is applied
  // before adding "open", otherwise the browser skips straight to the
  // final position instead of animating the slide-in.
  viewer.getBoundingClientRect();
  viewer.classList.add("open");
}

function closeSlideshow() {
  if (!slideshowElements) return;

  const viewer = slideshowElements.viewer;
  viewer.classList.remove("open");
  window.setTimeout(() => viewer.remove(), viewerSlideDurationMs);

  document.removeEventListener("keydown", handleSlideshowKeydown);
  slideshowElements = null;
  slideshowState = null;

  clearSlideshowUrlParams();
}

// Reflects the current slide/sub-image in the URL (as 1-based indices) so the
// page can be reloaded or shared directly into this position in the
// slideshow. Uses replaceState rather than pushState so that stepping
// through images doesn't flood browser history.
function updateSlideshowUrlParams(index: number, subIndex: number) {
  const url = new URL(window.location.href);
  url.searchParams.set("slideshow", "open");
  url.searchParams.set("image", `${index + 1}`);

  if (subIndex === 0) {
    url.searchParams.delete("subimage");
  } else {
    url.searchParams.set("subimage", `${subIndex + 1}`);
  }

  window.history.replaceState(null, "", url.toString());
}

function clearSlideshowUrlParams() {
  const url = new URL(window.location.href);
  url.searchParams.delete("slideshow");
  url.searchParams.delete("image");
  url.searchParams.delete("subimage");
  window.history.replaceState(null, "", url.toString());
}

function handleSlideshowKeydown(event: KeyboardEvent) {
  if (!slideshowState) return;

  if (event.key === "Escape") {
    closeSlideshow();
  } else if (event.key === "ArrowLeft") {
    navigateToImage(slideshowState.index - 1);
  } else if (event.key === "ArrowRight") {
    navigateToImage(slideshowState.index + 1);
  } else if (event.key === "ArrowUp") {
    navigateToSubImage(slideshowState.subIndex - 1);
  } else if (event.key === "ArrowDown") {
    navigateToSubImage(slideshowState.subIndex + 1);
  } else if (event.key === " ") {
    toggleSlideshowFocus();
  } else {
    return;
  }

  event.preventDefault();
}

// Swipe left/right steps through the images in the bottom filmstrip, the
// same as the left/right arrow keys. Swipe up/down steps through the
// sub-images of the current image, the same as the up/down arrow keys.
function attachSwipeHandlers(element: HTMLElement) {
  let startX = 0;
  let startY = 0;

  element.addEventListener("touchstart", (event) => {
    const touch = event.touches[0];
    startX = touch.clientX;
    startY = touch.clientY;
  });

  element.addEventListener("touchend", (event) => {
    if (!slideshowState) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - startX;
    const deltaY = touch.clientY - startY;

    if (
      Math.abs(deltaX) < swipeThresholdPx &&
      Math.abs(deltaY) < swipeThresholdPx
    ) {
      return;
    }

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      navigateToImage(slideshowState.index + (deltaX < 0 ? 1 : -1));
    } else {
      navigateToSubImage(slideshowState.subIndex + (deltaY < 0 ? 1 : -1));
    }
  });
}

function navigateToImage(newIndex: number) {
  if (!slideshowState) return;
  if (newIndex < 0 || newIndex >= slideshowState.images.length) return;

  fadeTopSection(() => {
    slideshowState!.index = newIndex;
    slideshowState!.subIndex = 0;
    updateSlideshowImage();
    updateSlideshowUrlParams(slideshowState!.index, slideshowState!.subIndex);
  });
}

function navigateToSubImage(newSubIndex: number) {
  if (!slideshowState) return;

  const currentImage = slideshowState.images[slideshowState.index];
  const subImagePaths = getSubImagePaths(currentImage);
  if (newSubIndex < 0 || newSubIndex >= subImagePaths.length) return;

  fadeTopSection(() => {
    slideshowState!.subIndex = newSubIndex;
    updateSlideshowSubImage();
    updateSlideshowUrlParams(slideshowState!.index, slideshowState!.subIndex);
  });
}

// Tapping the primary image hides the info, sub-image, and filmstrip
// sections so the primary image can scale up to fill more of the viewer.
// Navigation (keyboard, swipe, filmstrip clicks once revisible) is
// unaffected by this toggle.
function toggleSlideshowFocus() {
  if (!slideshowState || !slideshowElements) return;

  slideshowState.focused = !slideshowState.focused;
  slideshowElements.viewer.classList.toggle("focused", slideshowState.focused);
}

// Fades .slideshow-top out, then applies the change and fades it back in.
// The fade in/out isn't awaited or synchronized beyond the fixed delay, so
// fast repeated navigation can overlap fades rather than queue them.
function fadeTopSection(applyChange: () => void) {
  const top = slideshowElements?.top;
  if (!top) {
    applyChange();
    return;
  }

  top.classList.add("fade-out");
  window.setTimeout(() => {
    applyChange();
    top.classList.remove("fade-out");
  }, topFadeDelayMs);
}

function buildSlideshowElements(images: GalleryImage[]): SlideshowElements {
  const viewer = document.createElement("div");
  viewer.classList.add("slideshow-viewer");
  viewer.appendChild(createCloseButton());

  const top = document.createElement("div");
  top.classList.add("slideshow-top");

  const info = document.createElement("div");
  info.classList.add("slideshow-info");

  const main = document.createElement("div");
  main.classList.add("slideshow-main");
  const mainImg = document.createElement("img");
  main.appendChild(mainImg);
  attachSwipeHandlers(main);
  mainImg.addEventListener("click", () => toggleSlideshowFocus());

  const subthumbs = document.createElement("div");
  subthumbs.classList.add("slideshow-subthumbs");

  top.appendChild(info);
  top.appendChild(main);
  top.appendChild(subthumbs);
  viewer.appendChild(top);

  const bottom = document.createElement("div");
  bottom.classList.add("slideshow-bottom");

  const filmstrip = document.createElement("div");
  filmstrip.classList.add("slideshow-filmstrip");

  const filmstripThumbs = images.map((image, index) =>
    createFilmstripThumb(image, index),
  );
  filmstripThumbs.forEach((thumb) => filmstrip.appendChild(thumb));

  bottom.appendChild(filmstrip);
  viewer.appendChild(bottom);

  return {
    viewer,
    top,
    info,
    mainImg,
    subthumbs,
    subthumbElements: [],
    filmstripThumbs,
  };
}

function createCloseButton(): HTMLButtonElement {
  const button = document.createElement("button");
  button.classList.add("slideshow-close");
  button.setAttribute("aria-label", "Close");
  button.textContent = "×";
  button.addEventListener("click", () => closeSlideshow());
  return button;
}

function createFilmstripThumb(
  image: GalleryImage,
  index: number,
): HTMLDivElement {
  const thumb = document.createElement("div");
  thumb.classList.add("slideshow-filmstrip-thumb");

  const img = document.createElement("img");
  img.src = `${galleriesBase}${image.thumb || image.primary_image}`;
  img.alt = image.description || "";
  thumb.appendChild(img);

  thumb.addEventListener("click", () => navigateToImage(index));

  return thumb;
}

// Updates everything that depends on which GalleryImage is selected: the
// info block, the sub-image thumbnails, the main image, and which
// filmstrip thumbnail is marked selected. The filmstrip and sub-thumbnail
// containers themselves are never rebuilt so their scroll position is kept.
function updateSlideshowImage() {
  if (!slideshowElements || !slideshowState) return;

  const image = slideshowState.images[slideshowState.index];
  const subImagePaths = getSubImagePaths(image);

  updateSlideshowInfo(image);
  updateSlideshowSubThumbnails(subImagePaths);
  updateSlideshowSubImage();

  slideshowElements.filmstripThumbs.forEach((thumb, index) => {
    thumb.classList.toggle("selected", index === slideshowState!.index);
  });

  slideshowElements.filmstripThumbs[slideshowState.index]?.scrollIntoView({
    behavior: "smooth",
    block: "nearest",
    inline: "nearest",
  });
}

// Updates only the main image and sub-thumbnail selection, used when the
// user picks a different sub-image of the same GalleryImage.
function updateSlideshowSubImage() {
  if (!slideshowElements || !slideshowState) return;

  const image = slideshowState.images[slideshowState.index];
  const subImagePaths = getSubImagePaths(image);
  const selectedPath = subImagePaths[slideshowState.subIndex];

  slideshowElements.mainImg.src = toFullUrl(selectedPath);
  slideshowElements.mainImg.alt = image.description || "";

  slideshowElements.subthumbElements.forEach((thumb, index) => {
    thumb.classList.toggle("selected", index === slideshowState!.subIndex);
  });
}

function updateSlideshowInfo(image: GalleryImage) {
  if (!slideshowElements) return;
  const info = slideshowElements.info;
  info.innerHTML = "";

  const woodSpecies =
    image.wood_species && image.wood_species.length > 0
      ? image.wood_species.join(", ")
      : "";

  appendTextField(info, "Description", image.description);
  appendContributorField(info, image.contributor);
  appendTextField(info, "Wood species", woodSpecies);
  appendTextField(info, "Finish", image.finish);
  appendTextField(info, "Size", image.dimensions);

  info.appendChild(createCorrectionsLink(image));
}

function appendTextField(
  container: HTMLElement,
  label: string,
  value: string | undefined,
) {
  if (!value) return;
  const field = document.createElement("div");
  field.classList.add("slideshow-info-field");

  const fieldLabel = document.createElement("strong");
  fieldLabel.textContent = `${label}: `;
  field.appendChild(fieldLabel);
  field.appendChild(document.createTextNode(value));

  container.appendChild(field);
}

function appendContributorField(
  container: HTMLElement,
  contributor: string | string[],
) {
  const names = Array.isArray(contributor) ? contributor : [contributor];
  if (names.length === 0 || !names[0]) return;

  const field = document.createElement("div");
  field.classList.add("slideshow-info-field");

  const fieldLabel = document.createElement("strong");
  fieldLabel.textContent = "Contributor: ";
  field.appendChild(fieldLabel);

  names.forEach((name, index) => {
    if (index > 0) {
      field.appendChild(document.createTextNode(", "));
    }
    field.appendChild(createContributorLink(name));
  });

  container.appendChild(field);
}

function createContributorLink(name: string): Node {
  if (name === "Unknown") {
    return document.createTextNode(name);
  }

  const anchor = document.createElement("a");
  anchor.href = `/galleries?type=members&name=${name
    .toLowerCase()
    .split(" ")
    .join("-")}`;
  anchor.textContent = name;
  return anchor;
}

function createCorrectionsLink(image: GalleryImage): HTMLDivElement {
  const link = document.createElement("div");
  link.classList.add("slideshow-corrections-link");

  const anchor = document.createElement("a");
  anchor.href = buildCorrectionsFormUrl(image);
  anchor.target = "_blank";
  anchor.rel = "noopener noreferrer";

  const small = document.createElement("small");
  small.textContent = "Offer corrections";
  anchor.appendChild(small);

  link.appendChild(anchor);
  return link;
}

function updateSlideshowSubThumbnails(subImagePaths: string[]) {
  if (!slideshowElements) return;
  const subthumbs = slideshowElements.subthumbs;
  subthumbs.innerHTML = "";
  subthumbs.style.display = subImagePaths.length > 1 ? "" : "none";

  slideshowElements.subthumbElements = subImagePaths.map((path, index) => {
    const thumb = document.createElement("div");
    thumb.classList.add("slideshow-subthumb");

    const img = document.createElement("img");
    img.src = toFullUrl(toThumbPath(path));
    thumb.appendChild(img);

    thumb.addEventListener("click", () => navigateToSubImage(index));

    subthumbs.appendChild(thumb);
    return thumb;
  });
}
